import { Plus } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import {
    primaryText,
    secondaryText,
    greyTextColor,
    darkGreenTextColor,
    borderPrimary,
    placeholderPrimary,
    greenColorClass,
} from "../../../../../../styling/stylingHelper";
import { CustomIngredient } from "../../../../../../services/customIngredients/customIngredientsService";
import { eur } from "../../../../../orderhistory/utils";
import { SupplierLogo } from "../../../../../ui/SupplierLogo";
import { Platform } from "../../../../../../types/Platforms";
import SourceRestaurantFilter from "./SourceRestaurantFilter";
import SourceRestaurantBadge from "../SourceRestaurantBadge";

interface CustomIngredientsTabProps {
    ingredients: CustomIngredient[];
    selectedIngredient: CustomIngredient | null;
    onSelectIngredient: (ingredient: CustomIngredient) => void;
    quantityInputValue: string;
    onQuantityChange: (value: string, numericValue: number) => void;
    calculateEstimatedPrice: () => number | null;
    getProductPlatformDisplay: (platform: string, customPlatformName?: string) => { showLogo: boolean; displayName: string; platformForLogo?: Platform };
    formatCustomIngredientPrice: (ingredient: CustomIngredient) => string;
    onCreateClick: () => void;
    searchQuery: string;
    currentRestaurantId: string;
    automationReview?: boolean;
    hideSourceFilter?: boolean;
}

const CustomIngredientsTab: React.FC<CustomIngredientsTabProps> = ({
    ingredients,
    selectedIngredient,
    onSelectIngredient,
    quantityInputValue,
    onQuantityChange,
    calculateEstimatedPrice,
    getProductPlatformDisplay,
    formatCustomIngredientPrice,
    onCreateClick,
    searchQuery,
    currentRestaurantId,
    automationReview = false,
    hideSourceFilter = false,
}) => {
    const { t } = useTranslation();
    if (automationReview) return <div className="min-h-0 overflow-y-auto">
        {!hideSourceFilter && <div className="p-3"><SourceRestaurantFilter currentRestaurantId={currentRestaurantId} /></div>}
        <button type="button" onClick={onCreateClick} className={`flex min-h-[44px] w-full items-center gap-2 px-4 py-2 text-left text-xs hover:bg-cardLightGreen dark:hover:bg-cardLightGreen-dark ${primaryText}`}>
            <Plus size={14} />{searchQuery.trim() ? t("costPrice.automationRun.add.createNamedCustom", { name: searchQuery.trim() }) : t("costPrice.automationRun.add.createCustom")}
        </button>
        {ingredients.map(ingredient => <button key={ingredient.id} type="button" onClick={() => onSelectIngredient(ingredient)}
            className={`flex min-h-[60px] w-full items-center justify-between gap-3 border-t px-4 py-2 text-left hover:bg-cardLightGreen dark:hover:bg-cardLightGreen-dark ${borderPrimary}`}>
            <div className="min-w-0"><p className={`break-words text-sm ${primaryText}`}>{ingredient.productName}</p>
                <div className={`mt-0.5 flex flex-wrap gap-2 text-xs ${greyTextColor}`}><span>{getProductPlatformDisplay(ingredient.platform, ingredient.customPlatformName).displayName}</span><SourceRestaurantBadge restaurantName={(ingredient as any).sourceRestaurantName} /></div>
            </div>
            {ingredient.price !== 0 && <span className={`shrink-0 text-xs ${greyTextColor}`}>{formatCustomIngredientPrice(ingredient)}</span>}
        </button>)}
    </div>;
    return (
        <div className="flex flex-1 flex-col min-h-0 gap-3 overflow-y-auto">
            {!hideSourceFilter && <SourceRestaurantFilter currentRestaurantId={currentRestaurantId} />}
            <button
                type="button"
                onClick={onCreateClick}
                className={`w-full flex items-center justify-center gap-2 h-11 ${greenColorClass} rounded-lg text-[14px] font-semibold`}
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 4.16663V15.8333" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.16699 10H15.8337" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t("costPrice.automationRun.add.createCustom")}
            </button>
            {ingredients.length === 0 ? (
                <div className={`p-4 text-center ${greyTextColor}`}>
                    {searchQuery ? t("costPrice.automationRun.add.noCustomMatch") : t("costPrice.automationRun.add.noCustom")}
                </div>
            ) : (
                ingredients.map((ingredient) => {
                    const isSelected = selectedIngredient?.id === ingredient.id;
                    const platformDisplay = getProductPlatformDisplay(ingredient.platform, ingredient.customPlatformName);

                    return (
                        <button
                            key={ingredient.id}
                            type="button"
                            onClick={() => onSelectIngredient(ingredient)}
                            className={`w-full text-left rounded-lg border py-[11px] pl-3 pr-4 bg-background dark:bg-background-dark flex flex-col transition-colors ${
                                isSelected
                                    ? "border-darkGreenColor dark:border-darkGreenColor-dark"
                                    : borderPrimary
                            }`}
                        >
                            <div className="mb-3 flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                    <p className={`text-[16px] leading-6 font-semibold tracking-[-0.16px] ${primaryText}`}>
                                        {ingredient.productName}
                                    </p>
                                    <SourceRestaurantBadge restaurantName={(ingredient as any).sourceRestaurantName} />
                                </div>
                                {isSelected && !(automationReview && ingredient.price === 0) && (() => {
                                    const estimatedPrice = calculateEstimatedPrice();
                                    return estimatedPrice !== null ? (
                                        <p className={`text-[16px] leading-6 font-semibold ${darkGreenTextColor}`}>
                                            {eur(estimatedPrice)}
                                        </p>
                                    ) : null;
                                })()}
                            </div>

                            <div className="flex items-center justify-between gap-3">
                                <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                    {t("costPrice.automationRun.add.supplier")}
                                </p>
                                <div>
                                    {platformDisplay.showLogo && platformDisplay.platformForLogo ? (
                                        <SupplierLogo
                                            platform={platformDisplay.platformForLogo}
                                            size="medium"
                                            alt={platformDisplay.displayName}
                                        />
                                    ) : (
                                        <span className={`${secondaryText} text-[14px]`}>{platformDisplay.displayName}</span>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between gap-3 my-2">
                                <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                    {t("costPrice.automationRun.add.unitPrice")}
                                </p>
                                <p className={`${secondaryText} text-[14px] leading-5 font-medium`}>
                                    {automationReview && ingredient.price === 0 ? t("costPrice.automationRun.add.noPrice") : formatCustomIngredientPrice(ingredient)}
                                </p>
                            </div>

                            {isSelected && !automationReview && (
                                <div className="mt-3">
                                    <p className={`${secondaryText} mb-3 text-[16px] leading-5 font-medium`}>
                                        {t("costPrice.automationRun.add.amount")}
                                    </p>
                                    <div className="flex items-center justify-between gap-2 rounded-lg border border-borderGrey dark:border-borderGrey-dark bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 h-11">
                                        <input
                                            type="number"
                                            inputMode="decimal"
                                            min={0}
                                            step="any"
                                            placeholder={`0.0 ${ingredient.quantityInfo.stdUnit}`}
                                            value={quantityInputValue}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                                                    onQuantityChange(value, value === '' ? 0 : parseFloat(value) || 0);
                                                }
                                            }}
                                            className={`flex-1 bg-transparent dark:bg-transparent text-[14px] leading-5 font-medium ${secondaryText} ${placeholderPrimary} outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-moz-appearance]:textfield`}
                                        />
                                        <span className={`text-[14px] leading-5 font-medium ${secondaryText}`}>
                                            {ingredient.quantityInfo.stdUnit}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </button>
                    );
                })
            )}
        </div>
    );
};

export default CustomIngredientsTab;

